import { useState, useEffect } from "react";

const serviceStatus = {
  loading: "loading",
  complete: "complete",
  errored: "errored",
};

/**
 * An asynchronous function defined within a Service.
 * @typedef {function(): Promise<Object>} ServiceFunction
 */

/**
 * A function used to reformat an array of response data.
 * @template T
 * @typedef {function(Object): T} DataMapFunction
 */

/**
 * A utility function that handles an asynchronous service function call, given a data mapping and
 * returns an object with reformatted response data and the loading and error statuses.
 * @template T
 * @param {ServiceFunction} service - The service function to call
 * @param {DataMapFunction<T>} dataMap - The data mapping function used for reformatting of the data
 * @param {Array} [deps=[]] - An array of dependencies that will trigger a re-fetch when changed
 * @return {{data: T[] | null, loading: boolean, error: string | null}}
 */

export const ServiceOnMount = (service, dataMap, deps = []) => {
  const [status, setStatus] = useState(serviceStatus.loading);
  const [data, setData] = useState(null); // Changed from [] to null
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    service()
      .then((response) => {
        if (!isMounted) return;

        if (!response || typeof response !== "object") {
          throw new Error("Invalid response format.");
        }

        if (response.Error) {
          throw new Error(response?.ErrorDesc || "Unknown API error.");
        }

        // Handle both array and object responses
        const apiData = response?.Data ?? response;

        if (Array.isArray(apiData)) {
          const mappedData = apiData.map(dataMap);
          setData(mappedData);
        } else {
          // Handle object response
          const mappedData = dataMap(apiData);
          setData([mappedData]); // Wrap in array for consistency
        }

        setStatus(serviceStatus.complete);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("ServiceOnMount Error:", err.message, err);
        setError(err.message || "An error occurred");
        setStatus(serviceStatus.errored);
      });

    return () => {
      isMounted = false;
    };
  }, deps);

  return {
    loading: status === serviceStatus.loading,
    data,
    error,
  };
};
